import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { preferencesService } from '../services/preferences';
import { serviceConfigurationService } from '../services/serviceConfiguration';
import { notificationService } from '../services/notifications';
import {
  ServiceConfiguration,
  TranscriptionService,
  UserPreferences,
} from '../types';
import { useApp } from './AppContext';

interface PreferencesContextType {
  preferences: UserPreferences;
  loading: boolean;
  updatePreferences: (
    newPreferences: Partial<UserPreferences>
  ) => Promise<void>;
  updatePreference: (key: string, value: any) => Promise<void>;
  getSidebarState: (side: 'files' | 'tools') => boolean;
  setSidebarState: (
    side: 'files' | 'tools',
    collapsed: boolean
  ) => Promise<void>;
  getTheme: () => 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => Promise<void>;
  // Service configuration
  serviceConfiguration: ServiceConfiguration | null;
  serviceConfigLoading: boolean;
  serviceConfigError: string | null;
  getAvailableServices: () => TranscriptionService[];
  validateFile: (file: File) => { isValid: boolean; errors: string[] };
  refetchServiceConfiguration: () => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(
  undefined
);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

interface PreferencesProviderProps {
  children: React.ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({
  children,
}) => {
  const { state, dispatch } = useApp();
  const [preferences, setPreferences] = useState<UserPreferences>({});
  const [loading, setLoading] = useState(true);

  // Service configuration state
  const [serviceConfiguration, setServiceConfiguration] =
    useState<ServiceConfiguration | null>(null);
  const [serviceConfigLoading, setServiceConfigLoading] = useState(false);
  const [serviceConfigError, setServiceConfigError] = useState<string | null>(
    null
  );

  // Initialize preferences from user data when user is available
  useEffect(() => {
    if (state.user?.preferences) {
      setPreferences(state.user.preferences);
      setLoading(false);
    } else if (state.user) {
      // User is logged in but no preferences, set defaults
      const defaultPrefs: UserPreferences = {
        sidebar: {
          files_collapsed: false,
          tools_collapsed: false,
        },
        theme: 'light' as const,
      };
      setPreferences(defaultPrefs);
      setLoading(false);
    } else {
      // No user logged in
      setLoading(state.isAuthenticating);
    }
  }, [state.user, state.isAuthenticating]);

  // Fetch service configuration when user is available
  const fetchServiceConfiguration = useCallback(async () => {
    setServiceConfigLoading(true);
    setServiceConfigError(null);
    try {
      const config = await serviceConfigurationService.getConfiguration();
      setServiceConfiguration(config);
    } catch (error) {
      console.error('Failed to fetch service configuration:', error);
      setServiceConfigError('Failed to load service configuration');
    } finally {
      setServiceConfigLoading(false);
    }
  }, []);

  // Fetch service configuration when user is authenticated
  useEffect(() => {
    if (state.user && !serviceConfiguration && !serviceConfigLoading) {
      fetchServiceConfiguration();
    }
  }, [
    state.user,
    serviceConfiguration,
    serviceConfigLoading,
    fetchServiceConfiguration,
  ]);

  // Service configuration methods
  const getAvailableServices = useCallback((): TranscriptionService[] => {
    if (!serviceConfiguration) return [];
    return serviceConfigurationService.getAvailableServices(
      serviceConfiguration
    );
  }, [serviceConfiguration]);

  const validateFile = useCallback(
    (file: File): { isValid: boolean; errors: string[] } => {
      if (!serviceConfiguration) {
        return {
          isValid: false,
          errors: ['Service configuration not loaded'],
        };
      }
      return serviceConfigurationService.validateFile(
        file,
        serviceConfiguration
      );
    },
    [serviceConfiguration]
  );

  const refetchServiceConfiguration = useCallback(async () => {
    await fetchServiceConfiguration();
  }, [fetchServiceConfiguration]);

  const updatePreferences = useCallback(
    async (newPreferences: Partial<UserPreferences>) => {
      try {
        const updatedPrefs =
          await preferencesService.updatePreferences(newPreferences);

        // Extract the actual preferences data if it's wrapped in a response object
        const actualPreferences = (updatedPrefs as any).data || updatedPrefs;
        setPreferences(actualPreferences);

        // Update user data in AppContext with new preferences
        if (state.user) {
          dispatch({
            type: 'SET_USER',
            payload: {
              ...state.user,
              preferences: actualPreferences,
            },
          });
        }

        // Refetch service configuration if transcription service changed
        if (newPreferences.transcription_service) {
          await fetchServiceConfiguration();
        }
      } catch (error) {
        console.error('Failed to update preferences:', error);
        notificationService.error('Failed to save preferences');
        throw error;
      }
    },
    [state.user, dispatch, fetchServiceConfiguration]
  );

  const updatePreference = useCallback(
    async (key: string, value: any) => {
      try {
        const updatedPrefs = await preferencesService.updatePreference(
          key,
          value
        );

        // Extract the actual preferences data if it's wrapped in a response object
        const actualPreferences = (updatedPrefs as any).data || updatedPrefs;
        setPreferences(actualPreferences);

        // Update user data in AppContext with new preferences
        if (state.user) {
          dispatch({
            type: 'SET_USER',
            payload: {
              ...state.user,
              preferences: actualPreferences,
            },
          });
        }

        // Refetch service configuration if transcription service changed
        if (key === 'transcription_service') {
          await fetchServiceConfiguration();
        }
      } catch (error) {
        console.error(`Failed to update preference ${key}:`, error);
        notificationService.error(`Failed to save preference: ${key}`);
        throw error;
      }
    },
    [state.user, dispatch, fetchServiceConfiguration]
  );

  const getSidebarState = useCallback(
    (side: 'files' | 'tools'): boolean => {
      return preferences.sidebar?.[`${side}_collapsed`] ?? false;
    },
    [preferences]
  );

  const setSidebarState = useCallback(
    async (side: 'files' | 'tools', collapsed: boolean) => {
      try {
        const updatedPrefs = await preferencesService.updatePreferences({
          sidebar: {
            [`${side}_collapsed`]: collapsed,
          },
        });
        setPreferences(updatedPrefs);

        // Update user data in AppContext with new preferences
        if (state.user) {
          dispatch({
            type: 'SET_USER',
            payload: {
              ...state.user,
              preferences: updatedPrefs,
            },
          });
        }
      } catch (error) {
        console.error(`Failed to update ${side} sidebar state:`, error);
        notificationService.error(`Failed to save ${side} sidebar state`);
        throw error;
      }
    },
    [state.user, dispatch]
  );

  const getTheme = useCallback(() => {
    return preferences.theme ?? 'light';
  }, [preferences.theme]);

  const setTheme = useCallback(
    async (theme: 'light' | 'dark') => {
      try {
        const updatedPrefs = await preferencesService.updatePreferences({
          theme,
        });

        // Update user data in AppContext with new preferences
        if (state.user) {
          dispatch({
            type: 'SET_USER',
            payload: {
              ...state.user,
              preferences: updatedPrefs,
            },
          });
        }
      } catch (error) {
        console.error(`Failed to update theme:`, error);
        notificationService.error(`Failed to save theme preference`);
        throw error;
      }
    },
    [state.user, dispatch]
  );

  const value: PreferencesContextType = {
    preferences,
    loading,
    updatePreferences,
    updatePreference,
    getSidebarState,
    setSidebarState,
    getTheme,
    setTheme,
    serviceConfiguration,
    serviceConfigLoading,
    serviceConfigError,
    getAvailableServices,
    validateFile,
    refetchServiceConfiguration,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};
